import { nonEmptyTrimmed } from './spotName';

export const RESERVE_SPOT_API_VERSION = 'v3.0';

export const ReserveSpotErrorCode = {
  SPOT_ALREADY_TAKEN: 'SPOT_ALREADY_TAKEN',
  SPOT_NOT_BOOKABLE: 'SPOT_NOT_BOOKABLE'
} as const;

export type ReserveSpotErrorCode = (typeof ReserveSpotErrorCode)[keyof typeof ReserveSpotErrorCode];

export type ReserveSpotError = {
  code?: string | undefined;
  message?: string | undefined;
  errors: string[];
  messagesData: Record<string, unknown>;
};

export type ReserveSpotWireErrorBody = {
  code?: string;
  message?: string;
  errors?: string[];
  message_data?: Record<string, unknown>;
};

export type ReserveSpotWireBody = {
  old_spot_name: string;
};

export function buildReserveSpotUrl(baseUrl: string, bookingId: string, spotName: string): string {
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/${RESERVE_SPOT_API_VERSION}/bookings/${bookingId}/spots/${encodeURIComponent(
    spotName
  )}/reserve`;
}

export function buildReserveSpotBody(oldSpotName?: string | null): ReserveSpotWireBody | undefined {
  const resolved = nonEmptyTrimmed(oldSpotName);
  return resolved ? { old_spot_name: resolved } : undefined;
}

type ReserveSpotErrorLike = Partial<ReserveSpotError> & ReserveSpotWireErrorBody;

function isReserveSpotError(value: unknown): value is ReserveSpotErrorLike {
  return typeof value === 'object' && value !== null && ('errors' in value || 'code' in value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function normalizeReserveErrorBody(body: ReserveSpotWireErrorBody): ReserveSpotError {
  return {
    code: typeof body.code === 'string' ? body.code : undefined,
    message: typeof body.message === 'string' ? body.message : undefined,
    errors: Array.isArray(body.errors) ? body.errors : [],
    messagesData: asRecord(body.message_data)
  };
}

export function getReserveSpotError(error: unknown, fallbackErrorKey: string): ReserveSpotError {
  if (!isReserveSpotError(error)) {
    return { errors: [fallbackErrorKey], messagesData: {} };
  }
  const errors =
    Array.isArray(error.errors) && error.errors.length > 0 ? error.errors : [fallbackErrorKey];
  return {
    code: typeof error.code === 'string' ? error.code : undefined,
    message: typeof error.message === 'string' ? error.message : undefined,
    errors,
    messagesData: asRecord(error.messagesData ?? error.message_data)
  };
}

export function isSpotAlreadyTaken(error: unknown): boolean {
  return isReserveSpotError(error) && error.code === ReserveSpotErrorCode.SPOT_ALREADY_TAKEN;
}

export function isSpotNotBookable(error: unknown): boolean {
  return isReserveSpotError(error) && error.code === ReserveSpotErrorCode.SPOT_NOT_BOOKABLE;
}

export function requiresSpotReselection(error: unknown): boolean {
  return isSpotAlreadyTaken(error) || isSpotNotBookable(error);
}
